import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { SITE_NAME } from '@/composables/useSeo'
import type { RouteRecordRaw } from 'vue-router'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/HomeView.vue'),
    meta: {
      title: '',
      public: true,
    },
  },
  
  {
    path: '/projects',
    name: 'Projects',
    component: () => import('@/views/ProjectsView.vue'),
    meta: {
      title: 'Проекты',
      public: true,
    },
  },
  
  {
    path: '/media',
    name: 'Media',
    component: () => import('@/views/MediaView.vue'),
    meta: {
      title: 'Медиа',
      public: true,
    },
  },
  
  {
    path: '/blog',
    name: 'Blog',
    component: () => import('@/views/BlogView.vue'),
    meta: {
      title: 'Блог',
      public: true,
    },
  },
  
  {
    path: '/blog/:slug',
    name: 'Article',
    component: () => import('@/views/ArticleView.vue'),
    meta: {
      title: 'Статья',
      public: true,
    },
  },
  
  {
    path: '/category/:slug',
    name: 'Category',
    component: () => import('@/views/CategoryView.vue'),
    meta: {
      title: 'Категория',
      public: true,
    },
  },
  
  {
    path: '/tag/:slug',
    name: 'Tag',
    component: () => import('@/views/TagView.vue'),
    meta: {
      title: 'Тег',
      public: true,
    },
  },
  
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/LoginView.vue'),
    meta: {
      title: 'Вход',
      public: true,
      guestOnly: true,
    },
  },
  
  {
    path: '/admin-login',
    name: 'AdminLogin',
    component: () => import('@/views/AdminLoginView.vue'),
    meta: {
      title: 'Вход для админа',
      public: true,
      guestOnly: true,
    },
  },
  
  {
    path: '/diary',
    name: 'Diary',
    component: () => import('@/views/DiaryView.vue'),
    meta: {
      title: 'Мой дневник',
      requiresAuth: true,
    },
  },
  
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/views/ProfileView.vue'),
    meta: {
      title: 'Профиль',
      requiresAuth: true,
    },
  },
  
  {
    path: '/admin',
    name: 'Admin',
    component: () => import('@/views/admin/AdminLayout.vue'),
    meta: {
      title: 'Админ-панель',
      requiresAuth: true,
      requiresAdmin: true,
    },
    children: [
      {
        path: '',
        name: 'AdminDashboard',
        component: () => import('@/views/admin/DashboardView.vue'),
        meta: { title: 'Дашборд' },
      },
      {
        path: 'articles',
        name: 'AdminArticles',
        component: () => import('@/views/admin/ArticlesView.vue'),
        meta: { title: 'Статьи' },
      },
      {
        path: 'articles/new',
        name: 'AdminArticleCreate',
        component: () => import('@/views/admin/ArticleEditView.vue'),
        meta: { title: 'Новая статья' },
      },
      {
        path: 'articles/edit/:id',
        name: 'AdminArticleEdit',
        component: () => import('@/views/admin/ArticleEditView.vue'),
        meta: { title: 'Редактирование статьи' },
      },
      {
        path: 'comments',
        name: 'AdminComments',
        component: () => import('@/views/admin/CommentsView.vue'),
        meta: { title: 'Комментарии' },
      },
      {
        path: 'categories',
        name: 'AdminCategories',
        component: () => import('@/views/admin/CategoriesView.vue'),
        meta: { title: 'Категории' },
      },
      {
        path: 'tags',
        name: 'AdminTags',
        component: () => import('@/views/admin/TagsView.vue'),
        meta: { title: 'Теги' },
      },
      {
        path: 'projects',
        name: 'AdminProjects',
        component: () => import('@/views/admin/ProjectsAdminView.vue'),
        meta: { title: 'Управление проектами' },
      },
      {
        path: 'blog',
        name: 'AdminBlog',
        component: () => import('@/views/admin/BlogAdminView.vue'),
        meta: { title: 'Управление блогом' },
      },
      {
        path: 'media',
        name: 'AdminMedia',
        component: () => import('@/views/admin/MediaAdminView.vue'),
        meta: { title: 'Управление медиа' },
      },
    ],
  },
  
  /**
   * Языковые версии публичных страниц: /en/..., /zh/...
   *
   * Отдельная ветка, а не параметр в каждом маршруте: так русские адреса
   * остаются ровно теми, что уже проиндексированы, и ни один существующий
   * путь не меняет формы. Компоненты те же — язык они берут из адреса
   * через useLanguage.
   */
  {
    path: '/:lang(en|zh)',
    children: [
      { path: '', name: 'HomeI18n', component: () => import('@/views/HomeView.vue'), meta: { public: true } },
      { path: 'blog', name: 'BlogI18n', component: () => import('@/views/BlogView.vue'), meta: { public: true } },
      { path: 'blog/:slug', name: 'ArticleI18n', component: () => import('@/views/ArticleView.vue'), meta: { public: true } },
      { path: 'projects', name: 'ProjectsI18n', component: () => import('@/views/ProjectsView.vue'), meta: { public: true } },
      { path: 'media', name: 'MediaI18n', component: () => import('@/views/MediaView.vue'), meta: { public: true } },
      { path: 'category/:slug', name: 'CategoryI18n', component: () => import('@/views/CategoryView.vue'), meta: { public: true } },
      { path: 'tag/:slug', name: 'TagI18n', component: () => import('@/views/TagView.vue'), meta: { public: true } },
    ],
  },

  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFoundView.vue'),
    meta: {
      title: 'Страница не найдена',
      public: true,
    },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    }
    
    return { top: 0, behavior: 'smooth' }
  },
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  if (authStore.accessToken && !authStore.user) {
    await authStore.fetchCurrentUser()
  }
  
  const requiresAuth = to.meta.requiresAuth as boolean
  const requiresAdmin = to.meta.requiresAdmin as boolean
  const guestOnly = to.meta.guestOnly as boolean
  
  // Вход один на весь сайт — по одноразовому коду TOTP
  if (requiresAuth && !authStore.isAuthenticated) {
    return next({
      name: 'AdminLogin',
      query: { redirect: to.fullPath },
    })
  }
  
  if (guestOnly && authStore.isAuthenticated) {
    return next({ name: 'Home' })
  }
  
  if (requiresAdmin && !authStore.isAdmin) {
    return next({ name: 'Home' })
  }
  
  next()
})

/**
 * Заголовок вкладки на время перехода.
 *
 * Публичные страницы ставят себе title сами через useSeo — там он собирается
 * из данных материала. Здесь только промежуточное значение, чтобы во вкладке
 * не висел заголовок предыдущей страницы, пока грузится следующая.
 *
 * Раньше сюда подставлялось «Заголовок | DUSTAR.PRO». Домен в title —
 * замечание аудита: он съедает место в сниппете и ничего не добавляет,
 * поисковик и так показывает адрес отдельной строкой. Бренд теперь
 * «pole DUSTAR» и живёт в useSeo.
 */
router.afterEach((to) => {
  const title = to.meta.title as string | undefined
  document.title = title ? `${title} — ${SITE_NAME}` : SITE_NAME
})

/**
 * Просмотр страницы для Яндекс.Метрики.
 *
 * Счётчик в index.html срабатывает один раз, при загрузке документа. Дальше
 * SPA меняет маршруты без перезагрузки, и без этого хука в отчётах у каждого
 * визита была бы ровно одна страница — та, с которой человек зашёл.
 *
 * Первый переход пропускаем: его уже посчитал сам счётчик при инициализации,
 * иначе стартовая страница удваивается. Заголовок передаём отдельно, потому
 * что document.title к этому моменту уже новый, а Метрика читает его сама
 * только при обычной загрузке.
 */
const YM_ID = 111128641
let ymFirstNavigationDone = false

router.afterEach((to, from) => {
  const ym = (window as unknown as { ym?: (...args: unknown[]) => void }).ym
  if (!ym) return

  if (!ymFirstNavigationDone) {
    ymFirstNavigationDone = true
    // from.name пустое только на самом первом разборе адреса
    if (!from.name) return
  }

  ym(YM_ID, 'hit', to.fullPath, {
    title: document.title,
    referer: from.fullPath,
  })
})

export default router
